import { count, snakeCase } from 'moderndash'
import { WorkBook, WorkSheet } from 'xlsx'
import { Blueprint, BlueprintSheet, Column, Constraint, EnumConfig, Field, FieldType, ReferenceConfig } from './types'
import { sheet2cols } from './utils'
import { maxBy } from '../../../nap/max.by'

export type BlueprintOptions = {
  name: string
  enums: string[]
  uniques: string[]
  references: string[]
  requireds: string[]
}

// Create a blueprint sheet from an XLSX.WorkSheet
export function makeSheet({
  sheetName,
  sheet,
  options,
}: {
  sheetName: string
  sheet: WorkSheet
  options: BlueprintOptions
}): BlueprintSheet {
  // Get the columns
  const cols = sheet2cols(sheet)

  // Convert each column to a field
  const fields = cols.map((col, index) => col2field(sheetName, col, options, index))
  return {
    name: sheetName,
    slug: snakeCase(sheetName),
    fields,
  }
}

// Given a column (array of cells), create a field.
// The first cell is the header, the rest are values.
export function col2field(sheetName: string, col: Column, options: BlueprintOptions, columnIndex: number): Field {
  const { enums, uniques, references, requireds } = options

  const header = col[0]

  // The label is the string value of the header cell, or column + index
  // the key is its snake-cased version.
  const label = !header || !header.v ? `Column ${columnIndex}` : header.v.toString()
  const key = snakeCase(label)

  const values = col.slice(1)

  // Infer the type from the values, we'll pick the type that occurs most often.
  const typeCounts = count(values, (cell) => cell?.t)

  // If there are no values, default to string
  const typeCode = maxBy(Object.entries(typeCounts), ([, ct]) => ct) || ['s', 0]

  let type: FieldType
  switch (typeCode[0]) {
    case 's':
      type = 'string'
      break
    case 'n':
      type = 'number'
      break
    case 'd':
      type = 'date'
      break
    case 'b':
      type = 'boolean'
      break
    default:
      type = 'string'
      break
  }

  let config: EnumConfig | ReferenceConfig | undefined
  const constraints: Constraint[] = []

  // Check for enum
  if (enums && enums.includes(`${sheetName}:${label}`)) {
    type = 'enum'

    // Assume every valid enum value is represented.
    const labelSet = new Set(values.map((cell) => cell.v.toString()))
    config = {
      options: [...labelSet].map((label2) => ({
        value: snakeCase(label2),
        label,
      })),
    }
  } else if (references) {
    // Check for reference
    const reference = references.find((ref) => ref.startsWith(`${sheetName}:${label}:`))
    if (reference) {
      const [_sheetName, _sheetLabel, refSheetName, refLabel] = reference.split(':')
      type = 'reference'
      config = {
        ref: snakeCase(refSheetName),
        key: snakeCase(refLabel),
        relationship: 'has-one',
      }
    }
  }

  // Check for unique
  if (uniques && uniques.includes(`${sheetName}:${label}`)) {
    constraints.push({ type: 'unique' })
  }

  // Check for requireds
  if (requireds && requireds.includes(`${sheetName}:${label}`)) {
    constraints.push({ type: 'required' })
  }

  const field: Field = {
    key,
    label,
    type,
  }

  if (config) {
    field.config = config
  }

  if (constraints.length > 0) {
    field.constraints = constraints
  }

  return field
}

export function makeBlueprint(workbook: WorkBook, options: any): Blueprint {
  const blueprint: Blueprint = {
    name: options.name,
    sheets: [],
    actions: [
      {
        operation: 'downloadWorkbook',
        mode: 'foreground',
        label: 'Submit',
        primary: true,
      },
    ],
  }

  workbook.SheetNames.forEach((sheetName: string) => {
    const sheet = workbook.Sheets[sheetName]
    blueprint.sheets.push(
      makeSheet({
        sheetName,
        sheet,
        options,
      })
    )
  })

  return blueprint
}
